import { Box, Button, Stack, Typography } from '@mui/material';
import type { GamePurchaseLink } from '../lib/api/games-api-client';

const SECTION_TITLE = 'Where to buy';

/**
 * `<GamePurchaseLinks>` — "Where to buy" button strip on the Game
 * page.
 *
 * Renders one button per `purchase_links` row from
 * `GetGameByIdV1`. Every row is a VERIFIED store / product page
 * (never an Amazon search URL — Amazon rows wait for the
 * `affiliate-integration` PA-API wiring, which emits tagged
 * product deep-links and flips `affiliateTagApplied: true`). The
 * component is forward-compatible: it renders the same button
 * regardless of the tag flag; the flag only drives the optional
 * "affiliate link" disclosure badge visibility.
 *
 * When the `links` list is empty, the component renders `null` so
 * the Game page doesn't ship an empty "Where to buy" header.
 *
 * R3.7 — every function ≤20 non-comment lines.
 */

interface GamePurchaseLinksProps {
  readonly links: ReadonlyArray<GamePurchaseLink>;
}

export function GamePurchaseLinks({ links }: GamePurchaseLinksProps): JSX.Element | null {
  if (links.length === 0) return null;
  const anyTagged = links.some((l) => l.isAffiliate);
  return (
    <Box>
      <Typography variant="h2" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1.5 }}>
        {SECTION_TITLE}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        {links.map((l) => <PurchaseButton key={`${l.vendor}-${l.url}`} link={l} />)}
      </Stack>
      {anyTagged && <AffiliateDisclosure />}
    </Box>
  );
}

interface PurchaseButtonProps { readonly link: GamePurchaseLink; }

/**
 * One purchase link rendered as a button. Steam gets a contained
 * (filled) variant to match Valve's house styling; everything else
 * renders as an outlined button so no single vendor dominates the
 * strip when multiple are present.
 */
function PurchaseButton({ link }: PurchaseButtonProps): JSX.Element {
  const variant = link.vendor === 'steam' ? 'contained' : 'outlined';
  return (
    <Button
      href={link.url}
      target="_blank"
      rel={link.isAffiliate ? 'sponsored noopener noreferrer' : 'noopener noreferrer'}
      variant={variant}
      size="medium"
      sx={{ textTransform: 'none' }}
    >
      {link.label}
    </Button>
  );
}

/** Shown only when at least one link carries a platform-owned tracking tag. */
function AffiliateDisclosure(): JSX.Element {
  return (
    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
      Some of the links above are affiliate links. DVGUP may earn a commission when you purchase through them, at no extra cost to you.
    </Typography>
  );
}
